/**
 * Minimal .xlsx reader (Isaac, 2026-10-13): no dependency, runs in the browser
 * AND in Node (it only needs DecompressionStream, TextDecoder and Uint8Array),
 * so a spreadsheet can be read client-side without uploading it anywhere.
 *
 * An .xlsx is a zip of XML parts. This reads the zip's central directory,
 * inflates the parts it needs (shared strings, workbook, sheets) and returns
 * each sheet as rows of strings. Values only: formulas come back as their
 * cached result, dates as Excel serial numbers. Plenty for stock sheets and
 * eBay templates; not a general spreadsheet engine.
 */

export interface Sheet {
  name: string;
  rows: string[][];
}

const u16 = (b: Uint8Array, o: number) => (b[o] ?? 0) | ((b[o + 1] ?? 0) << 8);
const u32 = (b: Uint8Array, o: number) => u16(b, o) + u16(b, o + 2) * 65536;

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** name -> bytes for every file in the zip. */
async function unzip(buf: Uint8Array): Promise<Map<string, Uint8Array>> {
  // End of central directory: scan back for its signature.
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--) {
    if (u32(buf, i) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("Not an .xlsx file (zip directory not found)");
  const count = u16(buf, eocd + 10);
  let p = u32(buf, eocd + 16);
  const dec = new TextDecoder();
  const out = new Map<string, Uint8Array>();
  for (let n = 0; n < count; n++) {
    if (u32(buf, p) !== 0x02014b50) throw new Error("Corrupt .xlsx (bad zip entry)");
    const method = u16(buf, p + 10);
    const compSize = u32(buf, p + 20);
    const nameLen = u16(buf, p + 28);
    const extraLen = u16(buf, p + 30);
    const commentLen = u16(buf, p + 32);
    const localOffset = u32(buf, p + 42);
    const name = dec.decode(buf.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    // Only the XML parts are needed; skip images, themes and the like.
    if (!name.endsWith(".xml") && !name.endsWith(".rels")) continue;
    const dataStart = localOffset + 30 + u16(buf, localOffset + 26) + u16(buf, localOffset + 28);
    const raw = buf.subarray(dataStart, dataStart + compSize);
    out.set(name, method === 0 ? raw : method === 8 ? await inflateRaw(raw) : new Uint8Array());
  }
  return out;
}

function decodeEntities(s: string): string {
  return s
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(Number.parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

/** Concatenated text of every <t> in an XML fragment (rich-text runs included). */
function textOf(xml: string): string {
  let s = "";
  for (const m of xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)) s += m[1] ?? "";
  return decodeEntities(s);
}

function colIndex(ref: string): number {
  let n = 0;
  for (const ch of ref.replace(/[^A-Z]/g, "")) n = n * 26 + ch.charCodeAt(0) - 64;
  return n - 1;
}

function parseSheet(xml: string, shared: string[]): string[][] {
  const rows: string[][] = [];
  for (const rowM of xml.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const rn = Number(/\br="(\d+)"/.exec(rowM[1] ?? "")?.[1]);
    if (!rn) continue;
    const cells: string[] = [];
    for (const c of (rowM[2] ?? "").matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = c[1] ?? "";
      const ref = /\br="([A-Z]+\d+)"/.exec(attrs)?.[1];
      if (!ref) continue;
      const type = /\bt="([^"]*)"/.exec(attrs)?.[1];
      const inner = c[2] ?? "";
      let value = "";
      if (type === "inlineStr") value = textOf(inner);
      else {
        const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
        if (v !== undefined) value = type === "s" ? (shared[Number(v)] ?? "") : decodeEntities(v);
      }
      cells[colIndex(ref)] = value;
    }
    while (rows.length < rn - 1) rows.push([]);
    rows[rn - 1] = Array.from(cells, (x) => x ?? "");
  }
  return rows;
}

export async function readXlsx(input: ArrayBuffer | Uint8Array): Promise<Sheet[]> {
  const files = await unzip(input instanceof Uint8Array ? input : new Uint8Array(input));
  const text = (name: string) => {
    const b = files.get(name);
    return b ? new TextDecoder().decode(b) : "";
  };

  const shared: string[] = [];
  for (const si of text("xl/sharedStrings.xml").matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) {
    shared.push(textOf(si[1] ?? ""));
  }

  const rels = new Map<string, string>();
  for (const r of text("xl/_rels/workbook.xml.rels").matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const id = /\bId="([^"]*)"/.exec(r[1] ?? "")?.[1];
    const target = /\bTarget="([^"]*)"/.exec(r[1] ?? "")?.[1];
    if (id && target) rels.set(id, target.startsWith("/") ? target.slice(1) : `xl/${target}`);
  }

  const sheets: Sheet[] = [];
  for (const s of text("xl/workbook.xml").matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const name = decodeEntities(/\bname="([^"]*)"/.exec(s[1] ?? "")?.[1] ?? "");
    const rid = /\br:id="([^"]*)"/.exec(s[1] ?? "")?.[1];
    const path = rid ? rels.get(rid) : undefined;
    if (!path) continue;
    sheets.push({ name, rows: parseSheet(text(path), shared) });
  }
  if (sheets.length === 0) throw new Error("No sheets found in this file");
  return sheets;
}
